import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProducts } from '../contexts/ProductContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useSettings } from '../contexts/SettingsContext';
import { FaCartPlus, FaSearch, FaFilter, FaTimes, FaCalendar, FaClock, FaUsers, FaInfoCircle } from 'react-icons/fa';
import CountdownTimer from '../components/CountdownTimer';
import toast from 'react-hot-toast';

const Products = () => {
  const { getAvailableProducts } = useProducts();
  const { user } = useAuth();
  const { addToCart, getCartItem } = useCart();
  const { getNextDeliveryDate, isOrderingCurrentlyOpen } = useSettings();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showOrderInfo, setShowOrderInfo] = useState(false);
  const [addingToCart, setAddingToCart] = useState({});
  const navigate = useNavigate();
  
  const products = getAvailableProducts();
  const categories = ['all', ...new Set(products.map(p => p.category))];
  
  // Society pre-order specific data
  const deliveryDate = getNextDeliveryDate();
  
  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);
  
  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (!user) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">🔐</div>
        <h2>Please login to view products</h2>
        <p className="mb-6">Sign in to browse our pre-order menu and place your society orders!</p>
        <button 
          className="btn btn-primary btn-lg" 
          onClick={() => navigate('/login')}
        >
          Login
        </button>
      </div>
    );
  }

  const handleAddToCart = async (e, product) => {
    e.stopPropagation();
    
    // Check if item is already in cart
    const cartItem = getCartItem(product._id);
    
    if (cartItem) {
      // If item is in cart, navigate to cart
      navigate('/cart');
      return;
    }
    
    setAddingToCart(prev => ({ ...prev, [product._id]: true }));
    
    // Simulate a brief loading state for better UX
    setTimeout(() => {
      addToCart(product, 1);
      toast.success(`${product.name} added to pre-order!`);
      setAddingToCart(prev => ({ ...prev, [product._id]: false }));
    }, 300);
  };

  const handleCardClick = (id) => {
    navigate(`/product/${id}`);
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className="container">
        <div className="skeleton" style={{ height: '2rem', width: '200px', margin: '2rem auto' }}></div>
        <div className="skeleton" style={{ height: '3rem', width: '300px', margin: '1rem auto' }}></div>
        <div className="product-grid">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="product-card">
              <div className="skeleton" style={{ height: '200px', width: '100%' }}></div>
              <div className="p-4">
                <div className="skeleton" style={{ height: '1.5rem', width: '80%', marginBottom: '0.5rem' }}></div>
                <div className="skeleton" style={{ height: '1rem', width: '60%', marginBottom: '1rem' }}></div>
                <div className="skeleton" style={{ height: '3rem', width: '100%' }}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Pre-Order Information Banner */}
      <div className="card mb-4" style={{ 
        background: 'linear-gradient(90deg, var(--primary) 0%, var(--accent) 100%)',
        color: 'white',
        border: 'none',
        boxShadow: 'var(--shadow-lg)'
      }}>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h3 className="mb-2">
                <FaUsers className="me-2" />
                Society Pre-Order
              </h3>
              <p className="mb-0" style={{ color: '#ffffff', opacity: 0.9, fontWeight: '500' }}>Place your orders for the upcoming delivery cycle</p>
            </div>
            <button 
              className="btn btn-outline-light btn-sm"
              onClick={() => setShowOrderInfo(!showOrderInfo)}
            >
              <FaInfoCircle className="me-1" />
              Order Info
            </button>
          </div>
          
          {showOrderInfo && (
            <div className="mt-4 p-3 bg-white bg-opacity-20 rounded">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <CountdownTimer showWarning={false} lightTheme={true} />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-2">
                    <FaCalendar className="me-2 text-white" />
                    <strong className="text-white">Delivery Date:</strong>
                  </div>
                  <p className="mb-0 text-white fw-bold" style={{ color: 'white !important' }}>{deliveryDate.toLocaleDateString()} ({deliveryDate.toLocaleDateString('en-US', { weekday: 'long' })})</p>
                </div>
              </div>
              <div className="mt-3 p-2 bg-white bg-opacity-30 rounded border border-white border-opacity-50">
                <small className="text-white fw-bold">
                  <strong>Note:</strong> Orders are processed in batches. Late orders may be included in the next delivery cycle.
                </small>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-6">
        <h1>Pre-Order Menu</h1>
        <button 
          className="btn btn-outline-primary d-flex align-items-center gap-2"
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter />
          {showFilters ? 'Hide' : 'Show'} Filters
        </button>
      </div>
      
      {/* Search and Filters */}
      <div className={`mb-6 ${showFilters ? 'd-block' : 'd-none'}`}>
        <div className="card">
          <div className="card-body">
            {/* Search Bar */}
            <div className="form-group mb-4">
              <label htmlFor="search">Search Products</label>
              <div className="d-flex gap-2">
                <div className="d-flex align-items-center flex-1">
                  <FaSearch className="text-secondary" style={{ marginLeft: '1rem', position: 'absolute', zIndex: 1 }} />
                  <input
                    type="text"
                    id="search"
                    className="form-control"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or description..."
                    style={{ paddingLeft: '3rem' }}
                  />
                </div>
                {(searchQuery || selectedCategory !== 'all') && (
                  <button 
                    className="btn btn-secondary"
                    onClick={clearFilters}
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>

            {/* Category Filter */}
            <div className="form-group">
              <label>Categories</label>
              <div className="d-flex flex-wrap gap-2">
                {categories.map(category => (
                  <button
                    key={category}
                    className={`btn ${selectedCategory === category ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <p className="text-secondary mb-0">
          {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} available for pre-order
        </p>
        {(searchQuery || selectedCategory !== 'all') && (
          <button 
            className="btn btn-sm btn-outline-primary"
            onClick={clearFilters}
          >
            Clear all filters
          </button>
        )}
      </div>

      {/* Products Grid */}
      {filteredProducts.length > 0 ? (
        <div className="product-grid">
          {filteredProducts.map((product) => (
            <div
              key={product._id}
              className="product-card"
              style={{ cursor: 'pointer', position: 'relative' }}
              onClick={() => handleCardClick(product._id)}
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter') handleCardClick(product._id); }}
              aria-label={`View details for ${product.name}`}
            >
              {/* Pre-order badge */}
              <div className="position-absolute top-0 start-0 m-3">
                <span className="badge bg-success">Pre-Order</span>
              </div>
              
              <img
                src={product.image}
                alt={product.name}
                className="product-image"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/300x200?text=Food+Image';
                }}
              />
              <div className="product-info">
                <h3 className="product-title">{product.name}</h3>
                                  <p className="product-price">₹{product.price}</p>
                <p className="product-description">{product.description}</p>
                
                {/* Pre-order specific info */}
                <div className="mb-3 p-2 bg-light rounded">
                  <small className="text-secondary">
                    <FaCalendar className="me-1" />
                    Delivery: {deliveryDate.toLocaleDateString()}
                  </small>
                </div>
                
                <div className="d-flex gap-2 mt-auto" style={{ minHeight: '44px' }}>
                  <button
                    className={`btn btn-sm ${getCartItem(product._id) ? 'btn-primary' : 'btn-accent'}`}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 6, 
                      fontWeight: 600, 
                      fontSize: '0.9rem',
                      minWidth: '120px',
                      transition: 'all 0.2s ease',
                      transform: 'translateY(0)'
                    }}
                    onClick={e => handleAddToCart(e, product)}
                    tabIndex={0}
                    aria-label={getCartItem(product._id) ? `Go to cart to manage ${product.name}` : `Add ${product.name} to pre-order`}
                    disabled={!isOrderingCurrentlyOpen() || addingToCart[product._id]}
                    onMouseEnter={(e) => {
                      if (!addingToCart[product._id]) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = getCartItem(product._id) 
                          ? '0 4px 12px rgba(79, 70, 229, 0.3)' 
                          : '0 4px 12px rgba(16, 185, 129, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '';
                    }}
                  >
                    {addingToCart[product._id] ? (
                      <>
                        <div className="spinner-border spinner-border-sm" style={{ width: '1em', height: '1em' }}></div>
                        Adding...
                      </>
                    ) : getCartItem(product._id) ? (
                      <>
                        <FaCartPlus style={{ fontSize: '1em' }} /> Go to Cart
                      </>
                    ) : (
                      <>
                        <FaCartPlus style={{ fontSize: '1em' }} /> Add to Cart
                      </>
                    )}
                  </button>
                  <Link
                    to={`/product/${product._id}`}
                    className="btn btn-primary btn-sm flex-fill"
                    style={{ 
                      fontWeight: 600, 
                      fontSize: '0.9rem',
                      transition: 'all 0.2s ease',
                      transform: 'translateY(0)'
                    }}
                    onClick={e => e.stopPropagation()}
                    tabIndex={0}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(79, 70, 229, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '';
                    }}
                  >
                    View Details
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <h2>No products found</h2>
          <p className="mb-6">
            {searchQuery || selectedCategory !== 'all' 
              ? "Try adjusting your search or filter criteria"
              : "We're currently updating our pre-order menu. Please check back soon!"
            }
          </p>
          {(searchQuery || selectedCategory !== 'all') && (
            <button
              className="btn btn-primary btn-lg"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Products; 